import type { PresentPath } from './capabilities'
import type { ProjectorDisplay } from './presenter-messages'

/**
 * The console's presentation, start to end, as one pure state machine:
 * `transition(state, event)` gives the next state and the effects to run, as
 * data. The effects runner (`console-effects.ts`) runs them synchronously,
 * inside the click that caused them - opening a window and handing over full
 * screen each need that click.
 *
 * - `idle`: nothing presented; a hint may say why the last try did nothing.
 * - `here`: one screen only - this window is full screen, no second window.
 * - `opening`: the presentation window was opened (or this console reloaded
 *   while presenting) and has not reported yet.
 * - `live`: the presentation window reports; `present` turns false while it
 *   is gone (closed, or reloading - which the grace period waits for).
 * - `incompatible`: the presentation window runs another build.
 */

/** Something the trainer should know after a click. */
export type PresenterHint =
	| 'allowed-click-again'
	| 'denied'
	| 'popups-blocked'
	| 'drag-to-projector'
	| 'click-on-projector'

/** What a running presentation keeps across a reload of the console. */
export type SavedPresentation = {
	id: string
	startedAt: number
	blank: boolean
}

export type ConsoleState =
	| { phase: 'idle'; hint?: PresenterHint }
	| { phase: 'here' }
	| ({ phase: 'opening'; hint?: PresenterHint } & SavedPresentation)
	| ({
			phase: 'live'
			display: ProjectorDisplay
			screenLabel?: string
			present: boolean
			hint?: PresenterHint
	  } & SavedPresentation)
	| ({ phase: 'incompatible' } & SavedPresentation)

export type RunningState = Extract<
	ConsoleState,
	{ phase: 'opening' | 'live' | 'incompatible' }
>

export type ConsoleTimer = 'opening' | 'grace'

export type ConsoleEvent =
	/** Present, with what it does in this browser right now. */
	| { type: 'present'; path: PresentPath }
	/** Full screen in this window instead (browsers that cannot place). */
	| { type: 'present-here' }
	| { type: 'screens-answered'; granted: boolean; clickActive: boolean }
	| { type: 'window-opened'; id: string; at: number; placed: boolean }
	| { type: 'window-blocked' }
	| { type: 'restored'; saved: SavedPresentation }
	| {
			type: 'projector-display'
			display: ProjectorDisplay
			screenLabel?: string
	  }
	| { type: 'projector-present'; present: boolean }
	| { type: 'other-build' }
	| { type: 'timer-over'; timer: ConsoleTimer }
	| { type: 'show-fullscreen' }
	| { type: 'delegation-failed' }
	| { type: 'toggle-blank' }
	| { type: 'end' }
	| { type: 'fullscreen-here-changed'; on: boolean }

export type ConsoleEffect =
	/** Ask for the screens - the permission prompt; answers `screens-answered`. */
	| { type: 'ask-screens' }
	/** Answers `window-opened` or `window-blocked`. */
	| { type: 'open-window'; place: boolean }
	/** Answers `delegation-failed` when there is no window to hand it to. */
	| { type: 'delegate-fullscreen'; id: string }
	| { type: 'enter-fullscreen-here' }
	| { type: 'leave-fullscreen-here' }
	/** Tell the presentation window it is over, and close it. */
	| { type: 'close-window' }
	| { type: 'save'; saved: SavedPresentation | null }
	/** What the projector shows instead of the stage. */
	| { type: 'show-blank'; blank: boolean }
	| { type: 'start-timer'; timer: ConsoleTimer }
	| { type: 'cancel-timer'; timer: ConsoleTimer }

export type Transition = [ConsoleState, ConsoleEffect[]]

export const INITIAL_CONSOLE: ConsoleState = { phase: 'idle' }

export const isRunning = (state: ConsoleState): state is RunningState =>
	state.phase === 'opening' ||
	state.phase === 'live' ||
	state.phase === 'incompatible'

const saved = ({
	id,
	startedAt,
	blank,
}: SavedPresentation): SavedPresentation => ({
	id,
	startedAt,
	blank,
})

const stay = (state: ConsoleState): Transition => [state, []]

const end = (): Transition => [
	{ phase: 'idle' },
	[
		{ type: 'close-window' },
		{ type: 'save', saved: null },
		{ type: 'show-blank', blank: false },
		{ type: 'cancel-timer', timer: 'opening' },
		{ type: 'cancel-timer', timer: 'grace' },
	],
]

const onPresent = (path: PresentPath): Transition => {
	switch (path) {
		case 'single':
			return [{ phase: 'idle' }, [{ type: 'enter-fullscreen-here' }]]
		case 'ask':
			return [{ phase: 'idle' }, [{ type: 'ask-screens' }]]
		case 'place':
			return [{ phase: 'idle' }, [{ type: 'open-window', place: true }]]
		case 'manual':
			return [{ phase: 'idle' }, [{ type: 'open-window', place: false }]]
	}
}

export const transition = (
	state: ConsoleState,
	event: ConsoleEvent
): Transition => {
	switch (event.type) {
		case 'present':
			return state.phase === 'idle' ? onPresent(event.path) : stay(state)

		case 'present-here':
			return state.phase === 'idle'
				? [{ phase: 'idle' }, [{ type: 'enter-fullscreen-here' }]]
				: stay(state)

		case 'screens-answered':
			if (state.phase !== 'idle') {
				return stay(state)
			}
			if (!event.granted) {
				return [{ phase: 'idle', hint: 'denied' }, []]
			}
			// The prompt took the click; open in it only if it is still live.
			return event.clickActive
				? [{ phase: 'idle' }, [{ type: 'open-window', place: true }]]
				: [{ phase: 'idle', hint: 'allowed-click-again' }, []]

		case 'window-blocked':
			return state.phase === 'idle'
				? [{ phase: 'idle', hint: 'popups-blocked' }, []]
				: stay(state)

		case 'window-opened': {
			if (state.phase !== 'idle') {
				return stay(state)
			}
			const presentation = { id: event.id, startedAt: event.at, blank: false }
			return [
				{
					phase: 'opening',
					...presentation,
					hint: event.placed ? undefined : 'drag-to-projector',
				},
				[
					{ type: 'save', saved: presentation },
					{ type: 'start-timer', timer: 'opening' },
				],
			]
		}

		case 'restored':
			return state.phase === 'idle'
				? [
						{ phase: 'opening', ...saved(event.saved) },
						[
							{ type: 'show-blank', blank: event.saved.blank },
							{ type: 'start-timer', timer: 'opening' },
						],
				  ]
				: stay(state)

		case 'projector-display':
			if (state.phase !== 'opening' && state.phase !== 'live') {
				return stay(state)
			}
			return [
				{
					phase: 'live',
					...saved(state),
					display: event.display,
					screenLabel: event.screenLabel,
					present: true,
					// Full screen on the projector: there is nothing left to do.
					hint: event.display === 'fullscreen' ? undefined : state.hint,
				},
				[
					{ type: 'cancel-timer', timer: 'opening' },
					{ type: 'cancel-timer', timer: 'grace' },
				],
			]

		case 'projector-present':
			if (state.phase === 'live') {
				return event.present
					? [
							{ ...state, present: true },
							[{ type: 'cancel-timer', timer: 'grace' }],
					  ]
					: [
							{ ...state, present: false },
							[{ type: 'start-timer', timer: 'grace' }],
					  ]
			}
			// A window of another build that went away takes the presentation
			// with it; one still opening is covered by the opening timer.
			return state.phase === 'incompatible' && !event.present
				? end()
				: stay(state)

		case 'other-build':
			return state.phase === 'opening' || state.phase === 'live'
				? [
						{ phase: 'incompatible', ...saved(state) },
						[
							{ type: 'cancel-timer', timer: 'opening' },
							{ type: 'cancel-timer', timer: 'grace' },
						],
				  ]
				: stay(state)

		case 'timer-over':
			if (event.timer === 'opening') {
				return state.phase === 'opening' ? end() : stay(state)
			}
			return state.phase === 'live' && !state.present ? end() : stay(state)

		case 'show-fullscreen':
			return state.phase === 'opening' || state.phase === 'live'
				? [
						{ ...state, hint: undefined },
						[{ type: 'delegate-fullscreen', id: state.id }],
				  ]
				: stay(state)

		case 'delegation-failed':
			return state.phase === 'opening' || state.phase === 'live'
				? [{ ...state, hint: 'click-on-projector' }, []]
				: stay(state)

		case 'toggle-blank': {
			if (state.phase !== 'opening' && state.phase !== 'live') {
				return stay(state)
			}
			const next = { ...state, blank: !state.blank }
			return [
				next,
				[
					{ type: 'show-blank', blank: next.blank },
					{ type: 'save', saved: saved(next) },
				],
			]
		}

		case 'end':
			if (isRunning(state)) {
				return end()
			}
			return state.phase === 'here'
				? [state, [{ type: 'leave-fullscreen-here' }]]
				: [{ phase: 'idle' }, []]

		case 'fullscreen-here-changed':
			if (state.phase === 'idle' && event.on) {
				return [{ phase: 'here' }, []]
			}
			return state.phase === 'here' && !event.on
				? [{ phase: 'idle' }, []]
				: stay(state)
	}
}
